import axios from "./axiosConfig";
import type { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import config from "../config";
import { clearToken } from "../slices/authSlice";
import { getFallbackMessage, getMessageFromPayload } from "./apiMessage";
import { unsubscribeLocalWebPushSubscription } from "./pushSubscriptionCleanup";

type Dispatch = (action: any) => void;

type ErrorPayload = {
  message?: string;
  code?: string | number;
  [key: string]: any;
};

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _csrfRetry?: boolean;
};

let apiDispatch: Dispatch | null = null;

export const setApiDispatch = (dispatch: Dispatch) => {
  apiDispatch = dispatch;
};

const { api } = config;
const API_PREFIX = (api.API_URL ?? "").replace(/\/+$/, "");

const buildUrl = (url: string): string => {
  if (/^https?:\/\//.test(url)) {
    return url;
  }

  const normalizedPath = url.startsWith("/") ? url : `/${url}`;

  return `${API_PREFIX}${normalizedPath}`;
};

const PUBLIC_PAGE_PATHS = [
  "/login",
  "/email-verified",
  "/register",
  "/forgot-password",
] as const;

const PUBLIC_API_PATHS = [
  "/web",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/sanctum/csrf-cookie",
] as const;

const PUBLIC_PAGE_PREFIXES = ["/password-reset/", "/web-menu/"] as const;

const getPathname = (value?: string): string => {
  if (!value) return "";

  try {
    const baseUrl = typeof window !== "undefined" ? window.location.origin : API_PREFIX || "http://localhost";
    return new URL(value, baseUrl).pathname;
  } catch {
    return value.startsWith("/") ? value.split("?")[0] : `/${value.split("?")[0]}`;
  }
};

const isPublicPage = (pathname: string): boolean =>
  PUBLIC_PAGE_PATHS.includes(pathname as (typeof PUBLIC_PAGE_PATHS)[number]) ||
  PUBLIC_PAGE_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const isPublicApiRequest = (requestUrl?: string): boolean => {
  const pathname = getPathname(requestUrl);
  return PUBLIC_API_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
};

const refreshCsrfCookie = () => axios.get(buildUrl("/sanctum/csrf-cookie"), { withCredentials: true });

const expireProtectedSession = () => {
  void unsubscribeLocalWebPushSubscription().catch((cleanupError) => {
    console.warn("Failed to clean up local web push subscription after auth expiry:", cleanupError);
  });
  apiDispatch?.(clearToken());
  window.localStorage.removeItem("authUser");
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

const getLoggedinUser = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const user = window.localStorage.getItem("authUser");

  if (!user) {
    return null;
  }

  try {
    return JSON.parse(user);
  } catch {
    window.localStorage.removeItem("authUser");
    return null;
  }
};

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  async function (error: AxiosError) {
    const responseData = error.response?.data as ErrorPayload | undefined;
    const status = error.response?.status as number | undefined;
    const requestConfig = error.config as RetriableRequestConfig | undefined;
    const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
    const publicContext = isPublicPage(currentPath) || isPublicApiRequest(requestConfig?.url);

    if (
      status === 419 &&
      requestConfig &&
      !requestConfig._csrfRetry &&
      getPathname(requestConfig.url) !== "/sanctum/csrf-cookie"
    ) {
      requestConfig._csrfRetry = true;
      try {
        await refreshCsrfCookie();
        return axios(requestConfig);
      } catch {
        // Continue to normalized error handling below. Protected routes will expire session.
      }
    }

    if ((status === 401 || status === 419) && !publicContext && typeof window !== "undefined") {
      expireProtectedSession();
    }

    const backendMessage = getMessageFromPayload(responseData);
    const fallbackMessage = getFallbackMessage({ status, code: responseData?.code });
    const resolvedMessage = status === 403 ? fallbackMessage : backendMessage ?? fallbackMessage;

    const normalizedError: ErrorPayload =
      responseData && typeof responseData === "object"
        ? {
            ...responseData,
            message: resolvedMessage,
          }
        : {
            message: resolvedMessage,
            status,
          };

    return Promise.reject(normalizedError);
  }
);

class APIClient {
  /**
   * Fetches data from given url
   */

  get = (url: any, params?: Record<string, any>) => {
    const hasParams = Boolean(params && Object.keys(params).length > 0);

    if (!hasParams) {
      return axios.get(buildUrl(url));
    }

    return axios.get(buildUrl(url), { params });
  };
  /**
   * post given data to url
   */
  create = (url : any, data : any, config?: AxiosRequestConfig) => {
    const resolvedConfig: AxiosRequestConfig = { ...(config ?? {}) };

    if (typeof FormData !== "undefined" && data instanceof FormData) {
      resolvedConfig.headers = {
        ...(resolvedConfig.headers ?? {}),
        "Content-Type": "multipart/form-data",
      };
    }

    return axios.post(buildUrl(url), data, resolvedConfig);
  };
  /**
   * Updates data
   */
  update = (url : any, data : any) => {
    return axios.patch(buildUrl(url), data);
  };

  put = (url : any, data : any) => {
    return axios.put(buildUrl(url), data);
  };
  /**
   * Delete
   */
  delete = (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.delete(buildUrl(url), { ...config });
  };
}
export { APIClient, getLoggedinUser };
